import { useEffect, useId, useRef, useState, type FormEvent } from 'react'
import {
  applyOutcomeBatch,
  previewHistoricalImpact,
  previewOutcomeBatch,
  scoreDay,
  type IsoDate,
  type MatrixDocument,
  type HistoricalImpactPreview,
  type OutcomeBatchAction,
  type OutcomeBatchCellMode,
  type OutcomeBatchDateScope,
  type OutcomeBatchPreview,
} from '../../packages/domain/src/index'

const actions: ReadonlyArray<{ value: OutcomeBatchAction; label: string }> = [
  { value: 'T', label: 'T' },
  { value: 'W', label: 'W' },
  { value: 'C', label: 'C' },
  { value: 'B', label: 'B' },
  { value: 'clear', label: 'Clear' },
]

const weekdays = [
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
  { value: 0, label: 'Sunday' },
] as const

export interface TimelineBatchCounts {
  readonly changed: number
  readonly protected: number
  readonly skipped: number
}

export interface TimelineBatchScoreSnapshot {
  readonly score: number | null
  readonly numerator?: number
  readonly denominator?: number
}

export interface TimelineBatchRequest {
  readonly operationIds: readonly string[]
  readonly from: IsoDate
  readonly to: IsoDate
  readonly action: OutcomeBatchAction
  readonly dateScope: OutcomeBatchDateScope
  readonly cellMode: OutcomeBatchCellMode
  readonly weekdays: readonly number[] | null
  readonly note?: string
}

export type TimelineBatchVariant = 'general' | 'bye-period'

export interface TimelineBatchPreviewResult {
  readonly preview: OutcomeBatchPreview
  readonly note?: string | null
  readonly counts: TimelineBatchCounts
  readonly currentScoreImpact: {
    readonly before: TimelineBatchScoreSnapshot
    readonly after: TimelineBatchScoreSnapshot
  }
  readonly historicalImpact: HistoricalImpactPreview
}

export interface TimelineBatchControlsProps {
  readonly variant?: TimelineBatchVariant
  readonly document: MatrixDocument
  readonly trustedToday: IsoDate
  readonly selectedOperationIds: readonly string[]
  readonly initialFrom: IsoDate
  readonly initialTo: IsoDate
  readonly onPreview: (result: TimelineBatchPreviewResult) => void
  readonly previewProvider?: (input: TimelineBatchRequest) => Promise<TimelineBatchPreviewResult>
  readonly onApply?: (result: TimelineBatchPreviewResult) => Promise<void>
  readonly onPreviewCleared?: () => void
  readonly onBusyChange?: (busy: boolean) => void
  readonly onCancel: () => void
}

export function timelineBatchCounts(preview: OutcomeBatchPreview): TimelineBatchCounts {
  return {
    changed: preview.cells.length,
    protected: preview.skippedExisting + preview.skippedImportOnly + preview.skippedGeneratedBye,
    skipped: preview.skippedEmpty + preview.skippedNotDue + preview.skippedInactive + preview.skippedWeekday,
  }
}

function scoreSnapshot(document: MatrixDocument, trustedToday: IsoDate): TimelineBatchScoreSnapshot {
  const day = scoreDay(document, trustedToday, { asOf: trustedToday })
  return { score: day.score, numerator: day.numerator, denominator: day.denominator }
}

export function buildTimelineBatchPreview(
  document: MatrixDocument,
  trustedToday: IsoDate,
  input: {
    readonly operationIds: readonly string[]
    readonly from: IsoDate
    readonly to: IsoDate
    readonly action: OutcomeBatchAction
    readonly dateScope: OutcomeBatchDateScope
    readonly cellMode: OutcomeBatchCellMode
    readonly weekdays: readonly number[] | null
    readonly note?: string
  },
): TimelineBatchPreviewResult {
  const preview = previewOutcomeBatch(document, { ...input, asOf: trustedToday })
  const applied = applyOutcomeBatch(document, preview)
  const historicalImpact = previewHistoricalImpact(document, applied, trustedToday, { from: input.from })
  return {
    preview,
    note: input.note?.trim() || null,
    counts: timelineBatchCounts(preview),
    currentScoreImpact: {
      before: scoreSnapshot(document, trustedToday),
      after: scoreSnapshot(applied, trustedToday),
    },
    historicalImpact,
  }
}

function formatScore(score: number | null): string {
  if (score === null) return 'No score'
  return `${Number(score.toFixed(2))}%`
}

export function TimelineBatchControls({
  variant = 'general',
  document,
  trustedToday,
  selectedOperationIds,
  initialFrom,
  initialTo,
  onPreview,
  previewProvider,
  onApply,
  onPreviewCleared,
  onBusyChange,
  onCancel,
}: TimelineBatchControlsProps) {
  const controlId = useId()
  const [action, setAction] = useState<OutcomeBatchAction>('T')
  const [from, setFrom] = useState(initialFrom)
  const [to, setTo] = useState(initialTo)
  const [dateScope, setDateScope] = useState<OutcomeBatchDateScope>('due')
  const [cellMode, setCellMode] = useState<OutcomeBatchCellMode>('fill-blanks')
  const [selectedWeekdays, setSelectedWeekdays] = useState<readonly number[]>([])
  const [note, setNote] = useState('')
  const [result, setResult] = useState<TimelineBatchPreviewResult | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState<'preview' | 'apply' | null>(null)
  const busyRef = useRef(false)
  const generation = useRef(0)
  const busyChange = useRef(onBusyChange)
  const previewCleared = useRef(onPreviewCleared)
  busyChange.current = onBusyChange
  previewCleared.current = onPreviewCleared
  const selectedCount = new Set(selectedOperationIds).size
  const selectionKey = [...new Set(selectedOperationIds)].sort().join('\u0000')

  useEffect(() => {
    if (busyRef.current) return
    generation.current += 1
    setResult(null)
    setError(null)
    previewCleared.current?.()
  }, [document, trustedToday, selectionKey, variant])

  useEffect(() => {
    if (busyRef.current) return
    generation.current += 1
    setFrom(initialFrom)
    setTo(initialTo)
    setResult(null)
    setError(null)
    previewCleared.current?.()
  }, [initialFrom, initialTo])

  useEffect(() => () => {
    generation.current += 1
    busyChange.current?.(false)
  }, [])

  const setWorkflowBusy = (next: 'preview' | 'apply' | null) => {
    busyRef.current = next !== null
    setBusy(next)
    busyChange.current?.(next !== null)
  }

  const invalidatePreview = () => {
    if (busyRef.current) return
    generation.current += 1
    setResult(null)
    setError(null)
    onPreviewCleared?.()
  }

  const toggleWeekday = (value: number) => {
    invalidatePreview()
    setSelectedWeekdays((current) => current.includes(value)
      ? current.filter((weekday) => weekday !== value)
      : [...current, value].sort((left, right) => left - right))
  }

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    const input: TimelineBatchRequest = {
      operationIds: selectedOperationIds,
      from,
      to,
      action: variant === 'bye-period' ? 'B' : action,
      dateScope,
      cellMode,
      weekdays: selectedWeekdays.length === 0 ? null : selectedWeekdays,
      ...(variant === 'bye-period' ? { note } : {}),
    }
    if (!previewProvider) {
      try {
        const next = buildTimelineBatchPreview(document, trustedToday, input)
        setError(null)
        setResult(next)
        onPreview(next)
      } catch (caught) {
        setResult(null)
        setError(caught instanceof Error ? caught.message : 'Unable to preview this batch')
        onPreviewCleared?.()
      }
      return
    }
    const requestGeneration = ++generation.current
    setWorkflowBusy('preview')
    void previewProvider(input).then((next) => {
      if (generation.current !== requestGeneration) return
      setError(null)
      setResult(next)
      onPreview(next)
    }).catch((caught) => {
      if (generation.current !== requestGeneration) return
      setResult(null)
      setError(caught instanceof Error ? caught.message : 'Unable to preview this batch')
      onPreviewCleared?.()
    }).finally(() => {
      if (generation.current === requestGeneration) setWorkflowBusy(null)
    })
  }

  const apply = async () => {
    if (!result || !onApply) return
    setWorkflowBusy('apply')
    setError(null)
    try {
      await onApply(result)
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Unable to apply this batch')
    } finally {
      setWorkflowBusy(null)
    }
  }

  return (
    <form className="timeline-batch-controls" aria-labelledby={`${controlId}-heading`} onSubmit={submit}>
      <header>
        <h3 id={`${controlId}-heading`}>{variant === 'bye-period' ? 'Add Bye period' : 'Batch edit'}</h3>
        <p aria-live="polite">{selectedCount} operation{selectedCount === 1 ? '' : 's'} selected</p>
      </header>

      <fieldset className="timeline-batch-inputs" disabled={busy !== null}>
        <legend className="sr-only">Batch settings</legend>
      {variant === 'general' && <fieldset>
        <legend>Outcome</legend>
        {actions.map((option) => (
          <label key={option.value}>
            <input
              type="radio"
              name={`${controlId}-action`}
              value={option.value}
              checked={action === option.value}
              onChange={() => { invalidatePreview(); setAction(option.value) }}
            />
            {option.label}
          </label>
        ))}
      </fieldset>}

      <div>
        <label htmlFor={`${controlId}-from`}>From</label>
        <input id={`${controlId}-from`} type="date" required value={from} onChange={(event) => { invalidatePreview(); setFrom(event.target.value) }} />
        <label htmlFor={`${controlId}-to`}>To</label>
        <input id={`${controlId}-to`} type="date" required value={to} onChange={(event) => { invalidatePreview(); setTo(event.target.value) }} />
      </div>

      <details onToggle={invalidatePreview}>
        <summary>Targeting options</summary>
        <fieldset>
          <legend>Date scope</legend>
          <label>
            <input type="radio" name={`${controlId}-date-scope`} checked={dateScope === 'due'} onChange={() => { invalidatePreview(); setDateScope('due') }} />
            Due dates
          </label>
          <label>
            <input type="radio" name={`${controlId}-date-scope`} checked={dateScope === 'all'} onChange={() => { invalidatePreview(); setDateScope('all') }} />
            All dates
          </label>
        </fieldset>
        <fieldset>
          <legend>Existing outcomes</legend>
          <label>
            <input type="radio" name={`${controlId}-cell-mode`} checked={cellMode === 'fill-blanks'} onChange={() => { invalidatePreview(); setCellMode('fill-blanks') }} />
            Fill blanks
          </label>
          <label>
            <input type="radio" name={`${controlId}-cell-mode`} checked={cellMode === 'replace-existing'} onChange={() => { invalidatePreview(); setCellMode('replace-existing') }} />
            Replace existing
          </label>
        </fieldset>
        <fieldset>
          <legend>Weekdays</legend>
          <p>No weekday selection targets every weekday.</p>
          {weekdays.map((weekday) => (
            <label key={weekday.value}>
              <input
                type="checkbox"
                checked={selectedWeekdays.includes(weekday.value)}
                onChange={() => toggleWeekday(weekday.value)}
              />
              {weekday.label}
            </label>
          ))}
        </fieldset>
      </details>
      {variant === 'bye-period' && <label htmlFor={`${controlId}-note`}>Note (optional)<textarea id={`${controlId}-note`} maxLength={500} value={note} onChange={(event) => { invalidatePreview(); setNote(event.target.value) }} /></label>}
      </fieldset>

      {selectedCount === 0 && <p role="alert">Select at least one operation.</p>}
      {error && <p role="alert">{error}</p>}

      {result && (
        <section aria-label="Exact batch preview" aria-live="polite">
          <h4>Exact preview</h4>
          {result.note && <p><strong>Note:</strong> {result.note}</p>}
          <dl>
            <div><dt>Changed</dt><dd>{result.counts.changed}</dd></div>
            <div><dt>Protected</dt><dd>{result.counts.protected}</dd></div>
            <div><dt>Skipped</dt><dd>{result.counts.skipped}</dd></div>
          </dl>
          <details>
            <summary>Count breakdown</summary>
            <dl>
              <div><dt>Existing outcomes</dt><dd>{result.preview.skippedExisting}</dd></div>
              <div><dt>Import-only outcomes</dt><dd>{result.preview.skippedImportOnly}</dd></div>
              <div><dt>Generated Byes</dt><dd>{result.preview.skippedGeneratedBye}</dd></div>
              <div><dt>Empty cells</dt><dd>{result.preview.skippedEmpty}</dd></div>
              <div><dt>Not due</dt><dd>{result.preview.skippedNotDue}</dd></div>
              <div><dt>Inactive dates</dt><dd>{result.preview.skippedInactive}</dd></div>
              <div><dt>Filtered weekdays</dt><dd>{result.preview.skippedWeekday}</dd></div>
            </dl>
          </details>
          <p>
            Current score: {formatScore(result.currentScoreImpact.before.score)} to {formatScore(result.currentScoreImpact.after.score)}.
          </p>
          <dl>
            <div><dt>Historical score days changed</dt><dd>{result.historicalImpact.changedDays.length}</dd></div>
            {result.currentScoreImpact.before.numerator !== undefined && result.currentScoreImpact.before.denominator !== undefined && result.currentScoreImpact.after.numerator !== undefined && result.currentScoreImpact.after.denominator !== undefined && <div><dt>Current ratio</dt><dd>{result.currentScoreImpact.before.numerator}/{result.currentScoreImpact.before.denominator} to {result.currentScoreImpact.after.numerator}/{result.currentScoreImpact.after.denominator}</dd></div>}
            <div><dt>Accumulation</dt><dd>{Number(result.historicalImpact.accumulation.before.toFixed(2))} to {Number(result.historicalImpact.accumulation.after.toFixed(2))}</dd></div>
          </dl>
          <details>
            <summary>Trail impact</summary>
            <ul>{result.historicalImpact.trails.map((trail) => <li key={trail.days}>{trail.days} days: {formatScore(trail.before)} to {formatScore(trail.after)}</li>)}</ul>
          </details>
        </section>
      )}

      <footer>
        <button type="submit" disabled={selectedCount === 0 || busy !== null}>{busy === 'preview' ? 'Reviewing…' : 'Review exact changes'}</button>
        {result && onApply && <button type="button" disabled={busy !== null || result.counts.changed === 0} onClick={() => void apply()}>{busy === 'apply' ? 'Applying…' : variant === 'bye-period' ? `Apply ${result.counts.changed} ${result.counts.changed === 1 ? 'Bye' : 'Byes'}` : `Apply ${result.counts.changed} changes`}</button>}
        <button type="button" disabled={busy !== null} onClick={onCancel}>Cancel</button>
      </footer>
    </form>
  )
}
